import type { Libp2pBrowserPeer } from '@/net/peer';
import type { MinimalContentStore } from '@/services/container';
import type { IdentityService } from './IdentityService';
import type { IStorage } from '@/storage/IStorage';
import type { Post, PostImage, PostManifest, PostMedia, ChatMedia } from '@shared/types';
import type { ContentConfig, IChainClient } from '@shared/chain';
import { computeCid } from '@/content/cid';
import { compressImageTiers } from '@/content/image';
import { canonicalBytes } from '@/crypto/canonical';
import { bytesToBase64 } from '@/lib/format';
import { MeshLinkError, toMeshLinkError } from '@/lib/result';
import { createLogger } from '@/lib/logger';
import { CONTENT_MAX_IMAGES_PER_POST } from '@/config';
import { useFeedStore } from '@/store/feed';

const logger = createLogger('service:feed');

/**
 * 把「来自中继的任意形状对象」归一成合法的 `Post`。
 *
 * 为什么必须做：中继是共享存储，可能返回历史版本写入的旧结构
 * （如 `{id,author,name,text,images,ts}`），或缺字段/字段类型不对。
 * 直接 `as Post` 会让 `post.authorDid.slice(...)` 在渲染时抛异常 → **整页崩溃**。
 * 此处逐字段兜底，并就地复算 `manifestCid`（服务端不保存该字段，且不应被信任）。
 */
export function normalizeRemotePost(raw: unknown): Post | null {
  if (raw == null || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
  const num = (v: unknown, fallback: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);

  const id = str(p.id) || `post_${num(p.ts, Date.now())}`;
  const authorPeer = str(p.authorPeer) || str(p.author);
  const images: PostImage[] = Array.isArray(p.images)
    ? (p.images
        .filter((img): img is Record<string, unknown> => img != null && typeof img === 'object')
        .map((img) => ({
          cid: str(img.cid),
          thumbCid: str(img.thumbCid) || undefined,
          w: num(img.w, 0),
          h: num(img.h, 0),
          size: num(img.size, 0),
          mime: str(img.mime, 'application/octet-stream'),
        }))
        .filter((img) => img.cid.length > 0) as PostImage[])
    : [];

  /**
   * 复算 `manifestCid`。
   *
   * 必须连 `type` 一起还原：`canonicalBytes` 会把它算进内容字节
   * （发布方 `FeedService.publish` 正是用 `{type,id,authorDid,…,ts,sig}` 计算的）。
   * 漏掉 `type` 会算出**与发布方不同**的 CID，展示与校验都会对不上。
   */
  const manifest = {
    type: (p.type === 'image' || images.length > 0 ? 'image' : 'text') as PostManifest['type'],
    id,
    authorDid: str(p.authorDid),
    authorPeer,
    authorName: str(p.authorName) || str(p.name) || (authorPeer ? authorPeer.slice(0, 10) : '匿名用户'),
    text: str(p.text),
    images,
    media: (p.media != null && typeof p.media === 'object' ? p.media : undefined) as PostMedia | undefined,
    scope: p.scope === 'friends' || p.scope === 'private' ? p.scope : undefined,
    ts: num(p.ts, Date.now()),
    sig: str(p.sig) || str(p.signature),
  };

  let manifestCid = '';
  try {
    manifestCid = computeCid(canonicalBytes(manifest));
  } catch {
    manifestCid = str(p.manifestCid);
  }

  return {
    ...manifest,
    manifestCid,
    txHash: typeof p.txHash === 'string' ? p.txHash : undefined,
    blockHeight: typeof p.blockHeight === 'number' ? p.blockHeight : undefined,
    hidden: p.hidden === true ? true : undefined,
  } as unknown as Post;
}

export interface FeedServiceDeps {
  peer: Libp2pBrowserPeer;
  content: MinimalContentStore;
  storage: IStorage;
  identity: IdentityService;
  /** 链客户端：读取管理后台定义的内容大小配置（发布预检同源）。 */
  chain: IChainClient;
}

export class FeedService {
  constructor(private readonly deps: FeedServiceDeps) {}

  /** 内容大小配置（管理后台定义）—— 模块级缓存，读取失败回落默认。 */
  private contentConfig: ContentConfig | null = null;

  /** 拉取一次内容大小配置（RPC 不可达时返回 null，调用方回落默认上限）。 */
  private async loadContentConfig(): Promise<ContentConfig | null> {
    if (this.contentConfig) return this.contentConfig;
    try {
      this.contentConfig = await this.deps.chain.getContentConfig();
      return this.contentConfig;
    } catch {
      return null;
    }
  }

  /** 发布图文帖（纯文本也可）。图片经双档压缩后上传到中继内容协议并本地缓存。 */
  async publish(input: {
    text: string;
    images?: File[];
    media?: { file: File; kind: 'video' | 'audio' | 'live' };
    scope?: 'public' | 'friends' | 'private';
  }): Promise<Post> {
    const text = (input.text ?? '').trim();
    const files = input.images ?? [];
    if (!text && files.length === 0 && !input.media) throw new MeshLinkError('E_INVALID_INPUT', '帖子不能为空');
    if (files.length > CONTENT_MAX_IMAGES_PER_POST) {
      throw new MeshLinkError('E_INVALID_INPUT', `最多 ${CONTENT_MAX_IMAGES_PER_POST} 张图片`);
    }
    const cfg = (await this.loadContentConfig()) ?? {
      maxImageBytes: 4 * 1024 * 1024,
      maxVideoBytes: 4 * 1024 * 1024,
      maxAudioBytes: 4 * 1024 * 1024,
      maxOtherBytes: 4 * 1024 * 1024,
    };
    const keys = this.deps.identity.requireUnlocked();
    const meta = this.deps.identity.current;
    if (!meta) throw new MeshLinkError('E_LOCKED');

    const images: PostImage[] = [];
    for (const file of files) {
      const { thumb, full } = await compressImageTiers(file, cfg.maxImageBytes);
      const fullCid = (await this.deps.content.put(full.bytes, full.mime)) as string;
      const thumbCid = (await this.deps.content.put(thumb.bytes, thumb.mime)) as string;
      await this.deps.storage.blobs.put({
        cid: fullCid,
        bytes: full.bytes,
        mime: full.mime,
        size: full.bytes.length,
        ts: Date.now(),
        lastAccess: Date.now(),
      });
      await this.deps.storage.blobs.put({
        cid: thumbCid,
        bytes: thumb.bytes,
        mime: thumb.mime,
        size: thumb.bytes.length,
        ts: Date.now(),
        lastAccess: Date.now(),
      });
      images.push({ cid: fullCid, thumbCid, w: full.w, h: full.h, size: full.bytes.length, mime: full.mime });
    }

    // 媒体附件（视频/语音/直播）：原字节上传（不做压缩），本地 blobs 缓存
    let media: PostMedia | undefined;
    if (input.media) {
      const { file, kind } = input.media;
      if (!file || !file.size) throw new MeshLinkError('E_INVALID_INPUT', '媒体文件为空');
      // 按类型上限预检（管理后台配置；video→maxVideoBytes，audio→maxAudioBytes，直播→默认）
      const capBytes = kind === 'video' ? cfg.maxVideoBytes : kind === 'audio' ? cfg.maxAudioBytes : cfg.maxOtherBytes;
      if (file.size > capBytes) {
        throw new MeshLinkError('E_CONTENT_TOO_LARGE', `媒体超过 ${(capBytes / (1024 * 1024)).toFixed(1)}MB 上限`);
      }
      const bytes = new Uint8Array(await file.arrayBuffer());
      const cid = (await this.deps.content.put(bytes, file.type || 'application/octet-stream')) as string;
      await this.deps.storage.blobs.put({
        cid,
        bytes,
        mime: file.type || 'application/octet-stream',
        size: bytes.length,
        ts: Date.now(),
        lastAccess: Date.now(),
      });
      media = { cid, mime: file.type || 'application/octet-stream', kind, size: bytes.length, name: file.name };
    }

    const manifest: PostManifest = {
      type: images.length ? 'image' : 'text',
      id: `post_${keys.address}_${Date.now()}`,
      authorDid: meta.did,
      authorPeer: meta.peerId,
      authorName: meta.name,
      text,
      images,
      media,
      scope: input.scope && input.scope !== 'public' ? input.scope : undefined,
      ts: Date.now(),
      sig: '',
    };
    manifest.sig = bytesToBase64(this.deps.identity.sign(canonicalBytes(manifest)));

    // A-5 归一化：peer.feedPublish 底层 dialProtocol 失败会抛 libp2p 原生 Error
    // （非 MeshLinkError 实例）。不包裹会落到 UI 的 `instanceof` 分支被降级为 E_UNKNOWN，
    // 导致用户看到「出现未知错误」而非「所有中继暂不可用，消息已本地排队」（信息丢失）。
    // 控制流不变：仍在此处抛错，不吞异常。
    try {
      await this.deps.peer.feedPublish(manifest);
    } catch (err) {
      throw toMeshLinkError(err, 'E_RELAY_UNAVAILABLE');
    }

    const post: Post = { ...manifest, manifestCid: computeCid(canonicalBytes(manifest)) };
    await this.deps.storage.posts.put(post);
    useFeedStore.getState().prepend(post);
    logger.info('已发布帖子', { id: post.id, images: images.length });
    return post;
  }

  /** 拉取广场历史并与本地缓存合并。 */
  async loadHistory(): Promise<Post[]> {
    // A-5 归一化：同 publish，peer 层拨号失败抛原生 Error，需包裹为 MeshLinkError，
    // 否则 UI catch 会降级 E_UNKNOWN（见 FeedPage.tsx:117）。控制流不变，仍抛出，不静默回退本地缓存。
    let remoteRaw: unknown[];
    try {
      remoteRaw = await this.deps.peer.feedHistory();
    } catch (err) {
      throw toMeshLinkError(err, 'E_RELAY_UNAVAILABLE');
    }
    const local = await this.deps.storage.posts.all();
    const map = new Map<string, Post>();
    for (const p of local) map.set(p.id, p);
    for (const raw of remoteRaw) {
      const post = normalizeRemotePost(raw);
      if (post) map.set(post.id, post);
    }
    const merged = [...map.values()].sort((a, b) => b.ts - a.ts);
    useFeedStore.getState().setPosts(merged);
    return merged;
  }

  /** 订阅实时新帖（每次启动会话调用一次即可）。 */
  subscribe(cb: (posts: Post[]) => void): void {
    this.deps.peer.setFeedCallback((raw) => {
      const list = Array.isArray(raw) ? raw : [raw];
      const posts = list
        .map((item) => normalizeRemotePost(item))
        .filter((p): p is Post => p !== null);
      if (posts.length === 0) return;
      for (const p of posts) void this.deps.storage.posts.put(p);
      const store = useFeedStore.getState();
      for (const p of posts) store.prepend(p);
      cb(posts);
    });
  }

  /** 拉取图片字节：本地缓存优先，其次中继。返回 {bytes,mime} 或 null。 */
  /** 聊天媒体上传（v3.11/v3.12）：图片双档压缩、视频/音频/文件原字节；返回 ChatMedia（含本地缓存）。 */
  async uploadChatMedia(
    file: File,
    kind: 'image' | 'video' | 'file' | 'audio',
  ): Promise<ChatMedia> {
    const cfg = (await this.loadContentConfig()) ?? {
      maxImageBytes: 4 * 1024 * 1024,
      maxVideoBytes: 4 * 1024 * 1024,
      maxAudioBytes: 4 * 1024 * 1024,
      maxOtherBytes: 4 * 1024 * 1024,
    };
    if (kind === 'image') {
      const { full } = await compressImageTiers(file, cfg.maxImageBytes);
      const fullCid = (await this.deps.content.put(full.bytes, full.mime)) as string;
      await this.deps.storage.blobs.put({
        cid: fullCid,
        bytes: full.bytes,
        mime: full.mime,
        size: full.bytes.length,
        ts: Date.now(),
        lastAccess: Date.now(),
      });
      return {
        cid: fullCid,
        mime: full.mime,
        kind: 'image',
        size: full.bytes.length,
        name: file.name,
        width: full.w,
        height: full.h,
      };
    }
    // video / audio / file：原字节上传（不做压缩），本地 blobs 缓存
    if (!file.size) throw new MeshLinkError('E_INVALID_INPUT', '文件为空');
    const capBytes =
      kind === 'audio' ? cfg.maxAudioBytes : kind === 'video' ? cfg.maxVideoBytes : cfg.maxOtherBytes;
    if (file.size > capBytes) {
      throw new MeshLinkError(
        'E_CONTENT_TOO_LARGE',
        `文件超过 ${(capBytes / (1024 * 1024)).toFixed(1)}MB 上限`,
      );
    }
    // MIME 推断：上传通道可能不携带 type，按扩展名/kind 兜底（播放器依赖正确 mime）
    const ext = (file.name.split('.').pop() ?? '').toLowerCase();
    const mimeMap: Record<string, string> = {
      mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime', m4v: 'video/x-m4v',
      avi: 'video/x-msvideo', mkv: 'video/x-matroska', mp3: 'audio/mpeg', wav: 'audio/wav',
      ogg: 'audio/ogg', oga: 'audio/ogg', m4a: 'audio/mp4', aac: 'audio/aac',
      flac: 'audio/flac', opus: 'audio/opus', png: 'image/png', jpg: 'image/jpeg',
      jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', bmp: 'image/bmp',
    };
    const mime =
      file.type ||
      mimeMap[ext] ||
      (kind === 'video' ? 'video/mp4' : kind === 'audio' ? 'audio/wav' : 'application/octet-stream');
    const bytes = new Uint8Array(await file.arrayBuffer());
    const cid = (await this.deps.content.put(bytes, mime)) as string;
    await this.deps.storage.blobs.put({
      cid,
      bytes,
      mime,
      size: bytes.length,
      ts: Date.now(),
      lastAccess: Date.now(),
    });
    return {
      cid,
      mime,
      kind,
      size: bytes.length,
      name: file.name,
    };
  }

  async getImage(cid: string): Promise<{ bytes: Uint8Array; mime: string } | null> {
    const cached = await this.deps.storage.blobs.get(cid);
    if (cached) {
      await this.deps.storage.blobs.put({ ...cached, lastAccess: Date.now() }).catch(() => undefined);
      return { bytes: cached.bytes, mime: cached.mime };
    }
    const res = await this.deps.content.get(cid);
    if (res) {
      await this.deps.storage.blobs
        .put({ cid, bytes: res, mime: 'application/octet-stream', size: res.length, ts: Date.now(), lastAccess: Date.now() })
        .catch(() => undefined);
      return { bytes: res, mime: 'application/octet-stream' };
    }
    return null;
  }

  /** 本地点赞切换（链上点赞本期未实现，仅本地态）。 */
  async toggleLike(postId: string): Promise<boolean> {
    const store = useFeedStore.getState();
    const next = !store.isLiked(postId);
    store.setLiked(postId, next);
    return next;
  }

  // ─────────────── 评论（v3.6.4：P2P 广播 + 按帖历史 + 实时订阅） ───────────────

  /** 拉取某帖历史评论（来自中继，跨端一致）。 */
  async commentHistory(postCid: string): Promise<unknown[]> {
    try {
      return await this.deps.peer.commentHistory(postCid);
    } catch (err) {
      throw toMeshLinkError(err, 'E_RELAY_UNAVAILABLE');
    }
  }

  /** 发布一条评论（广播到中继，其他端实时可见；失败抛 E_RELAY_UNAVAILABLE）。 */
  async commentPublish(postCid: string, comment: unknown): Promise<unknown> {
    try {
      return await this.deps.peer.commentPublish(postCid, comment);
    } catch (err) {
      throw toMeshLinkError(err, 'E_RELAY_UNAVAILABLE');
    }
  }

  /** 订阅某帖评论实时流（打开评论面板时调用；回调收到中继广播的评论对象）。 */
  commentSubscribe(postCid: string, cb: (comment: unknown) => void): void {
    this.deps.peer.commentSubscribe(postCid, cb);
  }

  /** 取消某帖评论订阅（关闭评论面板时调用）。 */
  commentUnsubscribe(postCid: string): void {
    this.deps.peer.commentUnsubscribe(postCid);
  }
}

export default FeedService;
